import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import './consultas_ia.css';
import './usuarios_api.css';
import './principal.css';
import { CuentasOffice } from './EmpresasApi';
import { EtiquetaDashboard } from './dashboardNawaiam';

// Sección Usuarios API de un ambiente: las cuentas empresa habilitadas para el login federado de la API. Si un
// email no está acá (o está suspendido), la API le responde al integrador que se contacte con el área comercial.
const CAMPOS = 'id nombre email dashboard solicitante estado creadoEn creadoPor personas mailEnviado mailEn mailError';
const LISTA = gql`query L($entorno: String) { getUsuariosApi(entorno: $entorno) { base url error errorMensaje items { ${CAMPOS} } } }`;
const FICHA = gql`query F($entorno: String, $id: ID!) { getUsuarioApi(entorno: $entorno, id: $id) {
  usuario { ${CAMPOS} } errorDashboard
  dashboard { mail nombre perfil enPanel valida motivo }
  panel { id nombre email dob origen enDashboard } } }`;
const CREAR = gql`mutation C($entorno: String, $dashboard: String!, $nombre: String, $email: String!, $solicitante: String!) {
  crearUsuarioApi(entorno: $entorno, dashboard: $dashboard, nombre: $nombre, email: $email, solicitante: $solicitante) { ${CAMPOS} } }`;
const ESTADO = gql`mutation E($entorno: String, $id: ID!, $activo: Boolean!) { cambiarEstadoUsuarioApi(entorno: $entorno, id: $id, activo: $activo) { id estado } }`;
const REENVIAR = gql`mutation R($entorno: String, $id: ID!) { reenviarMailUsuarioApi(entorno: $entorno, id: $id) { id mailEnviado mailEn mailError } }`;
const PERSONAS = gql`mutation P($entorno: String, $id: ID!, $mails: [String!]!) {
  guardarPersonasUsuarioApi(entorno: $entorno, id: $id, mails: $mails) { agregadas reactivadas quitadas rechazadas { nombre email motivo } } }`;

const fecha = (v) => (v ? new Date(v).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const mensaje = (e) => e.graphQLErrors?.[0]?.message || e.message;
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

export default function UsuariosApi({ entorno }) {
  const { data, loading, error, refetch } = useQuery(LISTA, { variables: { entorno }, fetchPolicy: 'network-only' });
  const [nuevo, setNuevo] = useState(false);
  const [modo, setModo] = useState('office');   // dar de alta desde las cuentas de Office o a mano
  const [abierto, setAbierto] = useState(null);
  const [aviso, setAviso] = useState(null);
  const r = data?.getUsuariosApi;
  const xs = r?.items || [];

  return (
    <div className="ua">
      <p className="ua-intro">
        Las cuentas empresa que pueden usar la API en este ambiente. El integrador entra con el email de la cuenta; si no está acá o está
        suspendida, la API le responde que se contacte con el área comercial. Al dar de alta una cuenta, el solicitante recibe un mail.
      </p>
      {error && <div className="cs-aviso cs-aviso--error">{mensaje(error)}</div>}
      {r?.error && <div className="cs-aviso cs-aviso--error">{r.errorMensaje}</div>}
      {aviso && <div className={`cs-aviso ${aviso.tipo === 'error' ? 'cs-aviso--error' : 'cs-aviso--ok'}`}>{aviso.texto}</div>}

      <div className="ua-alta-cabeza">
        <div className="detail-section-title" style={{ margin: 0 }}>Dar de alta en la API</div>
        <div className="ua-modos" role="radiogroup" aria-label="Cómo dar de alta">
          {[['office', 'Desde las cuentas de Office'], ['manual', 'Manual']].map(([k, t]) => (
            <button key={k} type="button" role="radio" aria-checked={modo === k} className={`ua-modo ${modo === k ? 'ua-modo--activo' : ''}`}
              onClick={() => { setModo(k); setNuevo(k === 'manual'); setAviso(null); }}>{t}</button>
          ))}
        </div>
      </div>
      {modo === 'office' && <CuentasOffice entorno={entorno} onCambio={refetch} />}
      {modo === 'manual' && nuevo && <Alta entorno={entorno} onCancelar={() => { setNuevo(false); setModo('office'); }}
        onListo={async (u) => {
          setNuevo(false); setModo('office'); await refetch(); setAbierto(u.id);
          setAviso(u.mailEnviado
            ? { tipo: 'ok', texto: `Listo: ${u.email} es usuario API. Le mandamos el aviso a ${u.solicitante}. Ahora elegí sus personas.` }
            : { tipo: 'error', texto: `${u.email} quedó dado de alta, pero el mail a ${u.solicitante} no salió${u.mailError ? ` (${u.mailError})` : ''}. Podés reenviarlo desde su ficha.` });
        }} />}

      <div className="ua-barra" style={{ marginTop: 18 }}>
        <div className="detail-section-title" style={{ margin: 0 }}>Usuarios API</div>
        <span className="cia-nota">{loading ? 'Cargando…' : plural(xs.length, 'usuario API', 'usuarios API')}</span>
      </div>
      {xs.length > 0 && (
        <div className="panel-card">
          <div className="cia-tabla-scroll">
            <table className="cia-tabla">
              <thead><tr><th>Cuenta</th><th>Email</th><th>Dashboard Nawaiam</th><th>Solicitante</th><th>Estado</th><th className="num">Personas</th><th>Alta</th></tr></thead>
              <tbody>
                {xs.map((u) => (
                  <tr key={u.id} tabIndex={0} className={abierto === u.id ? 'cia-fila--elegida' : ''}
                    onClick={() => setAbierto(abierto === u.id ? null : u.id)} onKeyDown={(e) => { if (e.key === 'Enter') setAbierto(abierto === u.id ? null : u.id); }}>
                    <td><b>{u.nombre}</b></td>
                    <td>{u.email}</td>
                    <td><EtiquetaDashboard nombre={u.dashboard} compacta /></td>
                    <td>{u.solicitante || '—'}{u.solicitante && !u.mailEnviado && <span className="ua-marca ua-marca--alerta" title={u.mailError || ''}>mail sin enviar</span>}</td>
                    <td><span className={`ua-marca ${u.estado === 'activo' ? 'ua-marca--ok' : 'ua-marca--alerta'}`}>{u.estado === 'activo' ? 'Activo' : 'Suspendido'}</span></td>
                    <td className="num">{u.personas ?? '—'}</td>
                    <td>{fecha(u.creadoEn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!loading && !xs.length && !r?.error && !nuevo && <div className="panel-card ua-vacio">Todavía no hay usuarios API en este ambiente.</div>}
      {abierto && <Ficha key={abierto} entorno={entorno} id={abierto} onCambio={refetch} onCerrar={() => setAbierto(null)} />}
    </div>
  );
}

function Alta({ entorno, onCancelar, onListo }) {
  const [f, setF] = useState({ dashboard: '', nombre: '', email: '', solicitante: '' });
  const [error, setError] = useState(null);
  const [crear, { loading }] = useMutation(CREAR);
  const campo = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  const enviar = async (e) => {
    e.preventDefault(); setError(null);
    try {
      const u = (await crear({ variables: { entorno, dashboard: f.dashboard.trim(), nombre: f.nombre.trim() || null, email: f.email.trim(), solicitante: f.solicitante.trim() } })).data.crearUsuarioApi;
      onListo(u);
    } catch (err) { setError(mensaje(err)); }
  };
  return (
    <form className="panel-card ua-alta" onSubmit={enviar}>
      <div className="detail-section-title">Nuevo usuario API</div>
      <div className="ua-campos">
        <label>Dashboard de Nawaiam<input className="manual-input" required placeholder="clave del dashboard" {...campo('dashboard')} /></label>
        <label>Nombre de la cuenta<input className="manual-input" placeholder="si queda vacío, el del dashboard" {...campo('nombre')} /></label>
        <label>Email de la cuenta<input className="manual-input" type="email" required placeholder="con este email entra el integrador" {...campo('email')} /></label>
        <label>Email del solicitante<input className="manual-input" type="email" required placeholder="recibe el aviso del alta" {...campo('solicitante')} /></label>
      </div>
      <p className="cia-nota">La cuenta tiene que tener dashboard: una cuenta sin dashboard no sirve como usuario API. Los dos emails pasan por el control de emails.</p>
      {error && <div className="cs-aviso cs-aviso--error">{error}</div>}
      <div className="ua-botones">
        <button type="button" className="btn-outline" onClick={onCancelar} disabled={loading}>Cancelar</button>
        <button type="submit" className="btn-green" disabled={loading}>{loading ? 'Dando de alta…' : 'Dar de alta'}</button>
      </div>
    </form>
  );
}

function Ficha({ entorno, id, onCambio, onCerrar }) {
  const { data, loading, error, refetch } = useQuery(FICHA, { variables: { entorno, id }, fetchPolicy: 'network-only' });
  const [cambiarEstado, { loading: cambiando }] = useMutation(ESTADO);
  const [reenviar, { loading: reenviando }] = useMutation(REENVIAR);
  const [guardar, { loading: guardando }] = useMutation(PERSONAS);
  const [elegidas, setElegidas] = useState(null);
  const [aviso, setAviso] = useState(null);
  const d = data?.getUsuarioApi;
  const u = d?.usuario;
  useEffect(() => { if (d && elegidas === null) setElegidas(d.dashboard.filter((p) => p.enPanel).map((p) => p.mail)); }, [d, elegidas]);

  const accion = async (fn, ok) => {
    setAviso(null);
    try { const r = await fn(); await refetch(); onCambio(); if (ok) setAviso({ tipo: 'ok', texto: ok(r) }); } catch (e) { setAviso({ tipo: 'error', texto: mensaje(e) }); }
  };
  const alternar = (mail) => setElegidas(elegidas.includes(mail) ? elegidas.filter((x) => x !== mail) : [...elegidas, mail]);

  return (
    <div className="panel-card ua-ficha">
      <div className="ua-ficha-cabeza">
        <div>
          <div className="detail-section-title">{u ? u.nombre : 'Usuario API'}</div>
          {u && <div className="cia-nota">{u.email} · alta {fecha(u.creadoEn)}{u.creadoPor ? ` por ${u.creadoPor}` : ''}</div>}
          {u && <div className="op-fila-dash" style={{ marginTop: 4 }}><EtiquetaDashboard nombre={u.dashboard} /></div>}
        </div>
        <button className="btn-subtle" onClick={onCerrar}>Cerrar</button>
      </div>
      {loading && <div className="cia-nota">Cargando…</div>}
      {error && <div className="cs-aviso cs-aviso--error">{mensaje(error)}</div>}
      {aviso && <div className={`cs-aviso ${aviso.tipo === 'error' ? 'cs-aviso--error' : 'cs-aviso--ok'}`}>{aviso.texto}</div>}
      {u && (
        <>
          <div className="ua-acciones">
            <span className={`ua-marca ${u.estado === 'activo' ? 'ua-marca--ok' : 'ua-marca--alerta'}`}>{u.estado === 'activo' ? 'Activo: la API acepta su login' : 'Suspendido: la API lo rechaza'}</span>
            <button className={u.estado === 'activo' ? 'btn-outline' : 'btn-green'} disabled={cambiando}
              onClick={() => accion(() => cambiarEstado({ variables: { entorno, id, activo: u.estado !== 'activo' } }),
                () => (u.estado === 'activo' ? 'Suspendido: desde ahora la API le responde que se contacte con el área comercial.' : 'Reactivado: la API vuelve a aceptar su login.'))}>
              {u.estado === 'activo' ? 'Suspender' : 'Reactivar'}
            </button>
            {u.solicitante && (
              <button className="btn-outline" disabled={reenviando}
                onClick={() => accion(() => reenviar({ variables: { entorno, id } }), () => `Le reenviamos el aviso a ${u.solicitante}.`)}>
                {u.mailEnviado ? 'Reenviar aviso' : 'Mandar aviso'} a {u.solicitante}
              </button>
            )}
          </div>

          <div className="detail-section-title">Personas del dashboard</div>
          <p className="cia-nota">Las marcadas quedan en el panel de la cuenta y el integrador las ve en GET /v1/players, sin fecha de nacimiento hasta que el evaluador la cargue. Las que no pasan el control de nombres y emails no se pueden agregar.</p>
          {d.errorDashboard && <div className="cs-aviso cs-aviso--error">{d.errorDashboard}</div>}
          <div className="ua-personas">
            {d.dashboard.map((p) => (
              <label key={p.mail || p.nombre} className={`ua-persona ${!p.valida ? 'ua-persona--mala' : ''}`}>
                <input type="checkbox" disabled={!p.valida || elegidas === null} checked={!!elegidas && elegidas.includes(p.mail)} onChange={() => alternar(p.mail)} />
                <span><b>{p.nombre}</b> <span className="cia-nota">{p.mail}{p.perfil ? ` · ${p.perfil}` : ''}</span>
                  {!p.valida && <span className="ua-motivo">No se puede agregar: {p.motivo}</span>}</span>
              </label>
            ))}
            {!d.dashboard.length && !d.errorDashboard && <div className="cia-nota">El dashboard no devolvió personas.</div>}
          </div>
          <div className="ua-botones">
            <button className="btn-green" disabled={guardando || elegidas === null}
              onClick={() => accion(() => guardar({ variables: { entorno, id, mails: elegidas } }), (r) => {
                const x = r.data.guardarPersonasUsuarioApi;
                const partes = [`${x.agregadas + x.reactivadas} agregadas`, `${x.quitadas} quitadas`];
                return `Personas guardadas: ${partes.join(', ')}.${x.rechazadas.length ? ` No se agregaron ${x.rechazadas.length}: ${x.rechazadas.map((y) => `${y.nombre} (${y.motivo})`).join('; ')}.` : ''}`;
              })}>{guardando ? 'Guardando…' : 'Guardar personas'}</button>
          </div>
          {d.panel.some((p) => !p.enDashboard) && (
            <>
              <div className="detail-section-title">En el panel, fuera del dashboard</div>
              <ul className="ua-panel">{d.panel.filter((p) => !p.enDashboard).map((p) => <li key={p.id}><b>{p.nombre}</b> <span className="cia-nota">{p.email} · {p.origen || '—'}</span></li>)}</ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
